import { parseAgentSessionKey } from "../sessions/session-key-utils.js";

/** Four colon-derived segments (scope → agent → kind → id). */
export type SessionKeyPath = [string, string, string, string];

export type LogScopeFields = {
  scope?: string;
  agent?: string;
  kind?: string;
  id?: string;
  sessionKey?: string;
  area?: string;
  runId?: string;
  correlationId?: string;
};

export type LogScopeEntry = LogScopeFields & {
  subsystem?: string | null;
  message?: string | null;
  raw?: string | null;
  level?: string | null;
  time?: string | null;
};

export type LogsScopeFilterOptions = {
  path: SessionKeyPath;
  includePlatform?: boolean;
  areaFilters?: Set<string> | null;
};

const PLATFORM_SUBSYSTEM_PREFIXES = ["gateway", "gateway/ws", "ws", "soc"] as const;

const SESSION_KEY_RE = /\b(?:sessionKey|session_key)\s*[=:]\s*["']?([a-z0-9][a-z0-9_:.-]{2,120})/gi;
const AGENT_KEY_RE = /\bagent:([a-z0-9][a-z0-9_-]{0,63}:[a-z0-9][a-z0-9_:.-]{0,120})/gi;
const RUN_ID_RE = /\b(?:runId|run_id)\s*[=:]\s*["']?([a-zA-Z0-9_-]{4,64})/gi;

export function parseSessionKeyPath(sessionKey: string): SessionKeyPath {
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return ["", "", "", ""];
  }
  const parsed = parseAgentSessionKey(trimmed);
  if (parsed) {
    const colon = parsed.rest.indexOf(":");
    if (colon < 0) {
      return ["agent", parsed.agentId, parsed.rest, ""];
    }
    return ["agent", parsed.agentId, parsed.rest.slice(0, colon), parsed.rest.slice(colon + 1)];
  }
  const parts = trimmed.split(":").filter((p) => p.length > 0);
  return [parts[0] ?? "", parts[1] ?? "", parts[2] ?? "", parts.slice(3).join(":") || ""];
}

export function sessionKeyFromPath(path: SessionKeyPath): string | null {
  const [p0, p1, p2, p3] = path.map((s) => s.trim());
  if (!p0 || !p1 || !p2) {
    return null;
  }
  if (p0 === "agent") {
    return p3 ? `agent:${p1}:${p2}:${p3}` : `agent:${p1}:${p2}`;
  }
  if (!p3) {
    return p2 ? `${p0}:${p1}:${p2}` : p1 ? `${p0}:${p1}` : p0;
  }
  return `${p0}:${p1}:${p2}:${p3}`;
}

export function areaFromSubsystem(subsystem?: string | null): string | null {
  const trimmed = (subsystem ?? "").trim();
  if (!trimmed) {
    return null;
  }
  const slash = trimmed.indexOf("/");
  return (slash >= 0 ? trimmed.slice(0, slash) : trimmed).toLowerCase() || null;
}

export function isPlatformSubsystem(subsystem?: string | null): boolean {
  const normalized = (subsystem ?? "").trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return PLATFORM_SUBSYSTEM_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
}

function pathHasSelection(path: SessionKeyPath): boolean {
  return path.some((segment) => segment.trim().length > 0);
}

function pathMatchesEntry(path: SessionKeyPath, entry: LogScopeEntry): boolean {
  const entryPath: SessionKeyPath = entry.sessionKey
    ? parseSessionKeyPath(entry.sessionKey)
    : [entry.scope ?? "", entry.agent ?? "", entry.kind ?? "", entry.id ?? ""];

  for (let i = 0; i < 4; i++) {
    const want = (path[i] ?? "").trim();
    if (!want) {
      continue;
    }
    if ((entryPath[i] ?? "").trim() !== want) {
      return false;
    }
  }
  return true;
}

function haystackForHeuristic(entry: LogScopeEntry): string {
  return [entry.raw, entry.message, entry.sessionKey, entry.subsystem].filter(Boolean).join(" ");
}

function heuristicMatchesPath(path: SessionKeyPath, entry: LogScopeEntry): boolean {
  const prefix = sessionKeyFromPath(path);
  if (!prefix) {
    return false;
  }
  const haystack = haystackForHeuristic(entry).toLowerCase();
  const lowerPrefix = prefix.toLowerCase();
  if (haystack.includes(lowerPrefix)) {
    return true;
  }
  for (let i = 0; i < 4; i++) {
    const want = (path[i] ?? "").trim();
    if (!want) {
      continue;
    }
    if (!haystack.includes(want.toLowerCase())) {
      return false;
    }
  }
  return true;
}

export function entryMatchesLogsScope(
  entry: LogScopeEntry,
  options: LogsScopeFilterOptions,
): boolean {
  const { path, includePlatform = false, areaFilters } = options;
  const hasPath = pathHasSelection(path);

  if (areaFilters && areaFilters.size > 0) {
    const area = areaFromSubsystem(entry.subsystem) ?? "";
    if (!area || !areaFilters.has(area)) {
      return false;
    }
  }

  if (!hasPath) {
    return true;
  }

  const prefixKey = sessionKeyFromPath(path);
  if (prefixKey && entry.sessionKey?.trim()) {
    const entryKey = entry.sessionKey.trim().toLowerCase();
    const prefixLower = prefixKey.toLowerCase();
    if (entryKey === prefixLower || entryKey.startsWith(`${prefixLower}:`)) {
      return true;
    }
    return false;
  }

  const hasSessionSignal = Boolean(
    entry.sessionKey?.trim() ||
    entry.scope?.trim() ||
    entry.agent?.trim() ||
    entry.kind?.trim() ||
    entry.id?.trim(),
  );

  if (!hasSessionSignal && isPlatformSubsystem(entry.subsystem)) {
    return includePlatform;
  }

  if (pathMatchesEntry(path, entry)) {
    return true;
  }

  return heuristicMatchesPath(path, entry);
}

function extractMetaObject(candidate: unknown): Record<string, unknown> | null {
  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    return candidate as Record<string, unknown>;
  }
  if (typeof candidate !== "string") {
    return null;
  }
  const trimmed = candidate.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
}

function pickString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

function findSessionKeyInText(text: string): string | undefined {
  SESSION_KEY_RE.lastIndex = 0;
  const sessionMatch = SESSION_KEY_RE.exec(text);
  if (sessionMatch?.[1]) {
    return sessionMatch[1].trim();
  }
  AGENT_KEY_RE.lastIndex = 0;
  const agentMatch = AGENT_KEY_RE.exec(text);
  if (agentMatch?.[0]) {
    return agentMatch[0].trim();
  }
  return undefined;
}

export function extractScopeFromText(text: string): LogScopeFields {
  const fields: LogScopeFields = {};
  const sessionKey = findSessionKeyInText(text);
  if (sessionKey) {
    fields.sessionKey = sessionKey;
    const [scope, agent, kind, id] = parseSessionKeyPath(sessionKey);
    if (scope) {
      fields.scope = scope;
    }
    if (agent) {
      fields.agent = agent;
    }
    if (kind) {
      fields.kind = kind;
    }
    if (id) {
      fields.id = id;
    }
  }

  RUN_ID_RE.lastIndex = 0;
  const runMatch = RUN_ID_RE.exec(text);
  if (runMatch?.[1]) {
    fields.runId = runMatch[1];
  }

  const corrMatch = /\bcorrelationId\s*[=:]\s*["']?([a-zA-Z0-9_-]{4,64})/i.exec(text);
  if (corrMatch?.[1]) {
    fields.correlationId = corrMatch[1];
  }

  return fields;
}

export function mergeScopeFields(base: LogScopeFields, extra: LogScopeFields): LogScopeFields {
  return {
    scope: base.scope ?? extra.scope,
    agent: base.agent ?? extra.agent,
    kind: base.kind ?? extra.kind,
    id: base.id ?? extra.id,
    sessionKey: base.sessionKey ?? extra.sessionKey,
    area: base.area ?? extra.area,
    runId: base.runId ?? extra.runId,
    correlationId: base.correlationId ?? extra.correlationId,
  };
}

export function enrichLogScopeFields(params: {
  subsystem?: string | null;
  message?: string | null;
  raw?: string | null;
  metaObject?: Record<string, unknown> | null;
}): LogScopeFields {
  let fields: LogScopeFields = {};
  const area = areaFromSubsystem(params.subsystem);
  if (area) {
    fields.area = area;
  }

  if (params.metaObject) {
    const sessionKey = pickString(params.metaObject, ["sessionKey", "session_key"]);
    const agentId = pickString(params.metaObject, ["agentId", "agent_id"]);
    if (sessionKey) {
      fields = mergeScopeFields(fields, extractScopeFromText(sessionKey));
    } else if (agentId) {
      fields.agent = agentId;
      fields.scope = fields.scope ?? "agent";
    }
    const runId = pickString(params.metaObject, ["runId", "run_id"]);
    if (runId) {
      fields.runId = runId;
    }
    const correlationId = pickString(params.metaObject, ["correlationId", "correlation_id"]);
    if (correlationId) {
      fields.correlationId = correlationId;
    }
  }

  const textHaystack = [params.message, params.raw].filter(Boolean).join(" ");
  if (textHaystack) {
    fields = mergeScopeFields(fields, extractScopeFromText(textHaystack));
  }

  if (fields.sessionKey && !fields.scope) {
    const [scope, agent, kind, id] = parseSessionKeyPath(fields.sessionKey);
    fields = mergeScopeFields(fields, { scope, agent, kind, id });
  }

  if (!fields.area && area) {
    fields.area = area;
  }

  return fields;
}

export type ParsedLogRecord = {
  time?: string;
  level?: string;
  subsystem?: string;
  module?: string;
  message: string;
  raw: string;
  metaObject?: Record<string, unknown>;
};

export function applyScopeToParsedLine(line: ParsedLogRecord): ParsedLogRecord & LogScopeFields {
  const metaFromZero = line.metaObject;
  const scopeFields = enrichLogScopeFields({
    subsystem: line.subsystem,
    message: line.message,
    raw: line.raw,
    metaObject: metaFromZero,
  });
  return { ...line, ...scopeFields };
}

export function extractMetaFromLogObject(
  obj: Record<string, unknown>,
): Record<string, unknown> | null {
  const slot0 = obj["0"];
  const fromZero = extractMetaObject(slot0);
  if (
    fromZero &&
    (fromZero.subsystem || fromZero.module || fromZero.sessionKey || fromZero.agentId)
  ) {
    return fromZero;
  }
  if (fromZero && Object.keys(fromZero).length > 0 && !fromZero.subsystem && !fromZero.module) {
    return fromZero;
  }
  const meta =
    obj._meta && typeof obj._meta === "object" && obj._meta !== null
      ? (obj._meta as Record<string, unknown>)
      : null;
  if (typeof meta?.name === "string") {
    const nameMeta = extractMetaObject(meta.name);
    if (nameMeta) {
      return nameMeta;
    }
  }
  return fromZero;
}

export type LogsTailFilterParams = {
  subsystemPrefixes?: string[];
  sessionKeyPrefix?: string;
  levels?: string[];
  needle?: string;
  sinceMs?: number;
  includePlatform?: boolean;
};

function parseLogTimeMs(time?: string): number | null {
  if (!time) {
    return null;
  }
  const ms = Date.parse(time);
  return Number.isFinite(ms) ? ms : null;
}

export function lineMatchesTailFilters(
  line: ParsedLogRecord & LogScopeFields,
  filters: LogsTailFilterParams,
): boolean {
  if (filters.sinceMs != null && Number.isFinite(filters.sinceMs)) {
    const ts = parseLogTimeMs(line.time);
    if (ts != null && ts < filters.sinceMs) {
      return false;
    }
  }

  if (filters.levels && filters.levels.length > 0) {
    const level = (line.level ?? "").toLowerCase();
    if (!level || !filters.levels.map((l) => l.toLowerCase()).includes(level)) {
      return false;
    }
  }

  if (filters.subsystemPrefixes && filters.subsystemPrefixes.length > 0) {
    const subsystem = (line.subsystem ?? "").toLowerCase();
    const area = (line.area ?? areaFromSubsystem(line.subsystem) ?? "").toLowerCase();
    const ok = filters.subsystemPrefixes.some((prefix) => {
      const p = prefix.toLowerCase();
      return subsystem.startsWith(p) || area === p || subsystem === p;
    });
    if (!ok) {
      return false;
    }
  }

  if (filters.needle?.trim()) {
    const needle = filters.needle.trim().toLowerCase();
    const haystack = [line.message, line.subsystem, line.raw, line.sessionKey]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(needle)) {
      return false;
    }
  }

  if (filters.sessionKeyPrefix?.trim()) {
    const path = parseSessionKeyPath(filters.sessionKeyPrefix.trim());
    const includePlatform = filters.includePlatform === true;
    if (
      !entryMatchesLogsScope(line, {
        path,
        includePlatform,
        areaFilters: null,
      })
    ) {
      return false;
    }
  }

  return true;
}

export const DEFAULT_LOG_AREAS = [
  "agent",
  "gateway",
  "channels",
  "skills",
  "cron",
  "memprism",
] as const;
